import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Briefcase } from 'lucide-react';
import Button from '@mui/joy/Button';
import SvgIcon from '@mui/joy/SvgIcon';
import { styled } from '@mui/joy';

const loadingMessages = [
  "Analyzing your LinkedIn network...",
  "Matching your connections to job opportunities...",
  "Your LinkedIn connections work at 100+ companies. Let's check for job openings!",
  "Matching you with job openings where referrals could help..."
];

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

const ConnectionsUpload = ({ onUploadComplete, filters }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [location, setLocation] = useState(filters?.location || 'India');
  const [jobTitle, setJobTitle] = useState(filters?.job_title || '');

  useEffect(() => {
    if (filters) {
      setLocation(filters.location || 'India');
      setJobTitle(filters.job_title || '');
    }
  }, [filters]);

  useEffect(() => {
    let interval;
    if (uploading) {
      interval = setInterval(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [uploading]);

  const handleUpload = useCallback(async (formData) => {
    try {
      const apiUrl = window.ENV?.VITE_API_URL || import.meta.env?.VITE_API_URL || 'https://algoheads-com.onrender.com';
      let uploadUrl = `${apiUrl}/upload-connections`;
      const params = new URLSearchParams();

      if (location) params.append('location', location);
      if (jobTitle) params.append('job_title', jobTitle);
      if (params.toString()) uploadUrl += `?${params.toString()}`;

      console.log("Uploading to:", uploadUrl);

      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log("Upload successful:", result);
      return result;
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  }, [location, jobTitle]);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError('Please upload a LinkedIn connections.csv file');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', file);

      try {
        const result = await handleUpload(formData);
        console.log("Upload Result:", result);
        onUploadComplete(result);
      } catch (uploadError) {
        console.error("Upload failed:", uploadError);
        setError(uploadError.message || "Upload failed");
      }
    } finally {
      setUploading(false);
    }
  }, [onUploadComplete, handleUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv']
    },
    multiple: false,
    noClick: true, // Disable direct file picking when clicking on drop zone
    noKeyboard: true
  });

  return (
    <div className="w-full max-w-xl mx-auto">
      {/* Filter indicators */}
      {(location || jobTitle) && !uploading && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm justify-center">
          {location && (
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-600">
              <MapPin size={14} className="mr-1" />
              {location}
            </div>
          )}
          {jobTitle && (
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-indigo-50 text-indigo-600">
              <Briefcase size={14} className="mr-1" />
              {jobTitle}
            </div>
          )}
        </div>
      )}

      {/* Drag and drop zone */}
      <div
  {...getRootProps()}
  style={{
    border: '2px dotted black', // Dotted black border
    borderRadius: '12px', // Rounded corners
    padding: '24px',
    textAlign: 'center',
    transition: 'all 0.2s',
    backgroundColor: isDragActive ? '#f3f4f6' : 'transparent', // Light gray when active
    cursor: uploading ? 'default' : 'pointer'
  }}
>
  <input {...getInputProps()} disabled={uploading} />
        
        {uploading ? (
          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
            </div>
            <AnimatePresence mode="wait">
              <motion.p
                key={loadingMessages[loadingMessageIndex]}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.5 }}
                className="text-blue-600 font-medium"
              >
                {loadingMessages[loadingMessageIndex]}
              </motion.p>
            </AnimatePresence>
          </div>
        ) : (
          <Button
            component="label"
            role={undefined}
            tabIndex={-1}
            variant="outlined"
            color="neutral"
            startDecorator={
              <SvgIcon>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"
                  />
                </svg>
              </SvgIcon>
            }
          >
            Upload or Drop Files
            <VisuallyHiddenInput {...getInputProps()} />
          </Button>
        )}
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-3 text-center text-red-600 bg-red-50 p-2 rounded-md"
        >
          {error}
        </motion.div>
      )}
    </div>
  );
};

export default ConnectionsUpload;
